import type React from 'react'
import type {BreakpointSize} from '@primer/react-brand'
import {Image, useWindowSize} from '@primer/react-brand'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import AutoPlayButton, {TRANSITION_DURATION, type AutoPlayButtonProps} from '../AutoPlayButton/AutoPlayButton'
import {wait} from '../../utils/time'
import {SCREENS} from './HeroCarousel.data'
import useMotion from './HeroCarousel.motion'
import {checkPrefersReducedMotion} from '../../../../lib/utils/platform'

export interface HeroCarouselProps {
  currentScreenIndex: number
  onAutoPlayTimeout: (newScreenIndex: number) => void
  autoPlayButtonAriaLabel: AutoPlayButtonProps['ariaLabel']
}

const defaultProps: Partial<HeroCarouselProps> = {}

const HeroCarousel: React.FC<HeroCarouselProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {currentScreenIndex, onAutoPlayTimeout, autoPlayButtonAriaLabel} = initializedProps

  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [shouldReduceMotion, setShouldReduceMotion] = useState<boolean>(false)
  const [isVisualVisible, setIsVisualVisible] = useState<boolean>(true)
  const [visibleScreenIndex, setVisibleScreenIndex] = useState<number>(currentScreenIndex)
  const [isPaused, setIsPaused] = useState<boolean>(false)
  const currentScreenIndexRef = useRef<number>(currentScreenIndex)
  const visualRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const videoSrc = useRef<string>('')
  const currentVideoTime = useRef<number>(-1)
  const windowSize = useWindowSize()

  const isDesktopView = useMemo(
    () => ['large', 'xlarge', 'xxlarge'].includes(windowSize.currentBreakpointSize as BreakpointSize),
    [windowSize],
  )
  const visibleScreen = useMemo(() => SCREENS[visibleScreenIndex], [visibleScreenIndex])
  const visibleScreenDuration = useMemo(
    () => visibleScreen?.visuals.reduce((previousValue, visual) => previousValue + visual.duration, 0) || 0,
    [visibleScreen],
  )

  const {show, resume, pause} = useMotion({visual: visualRef, isDesktopView})

  const onVisualTimeout = useCallback(() => {
    onAutoPlayTimeout((currentScreenIndexRef.current + 1) % SCREENS.length)
  }, [onAutoPlayTimeout])

  const onAutoPlayStateChange = useCallback((shouldPause: boolean) => {
    setIsPaused(shouldPause)
  }, [])

  const onVideoTimeUpdate = useCallback((event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = event.currentTarget as HTMLVideoElement
    if (!video) return

    // Resync the video if we just switched desktop/mobile assets
    if (videoSrc.current !== video.src && currentVideoTime.current >= 0) {
      video.currentTime = currentVideoTime.current
    }

    videoSrc.current = video.src || ''
    currentVideoTime.current = video.currentTime || 0
  }, [])

  useEffect(() => {
    currentScreenIndexRef.current = currentScreenIndex

    // Add delay to allow the transition to complete
    const proceed = async () => {
      if (shouldReduceMotion) {
        setVisibleScreenIndex(currentScreenIndexRef.current)
        return
      }

      setIsVisualVisible(false)
      await wait((TRANSITION_DURATION / 2) * 1000)

      setVisibleScreenIndex(currentScreenIndexRef.current)
      await wait((TRANSITION_DURATION / 2) * 1000)

      setIsVisualVisible(true)
    }

    proceed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenIndex])

  useEffect(() => {
    if (!shouldReduceMotion && visibleScreen) show(visibleScreen.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScreenIndex])

  useEffect(() => {
    if (!isMounted) return
    setShouldReduceMotion(checkPrefersReducedMotion())
  }, [isMounted])

  useEffect(() => {
    // Pause immediately if the user prefers reduced motion
    if (shouldReduceMotion) onAutoPlayStateChange(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReduceMotion])

  useEffect(() => {
    const video = visualRef.current?.querySelector('video')
    if (video?.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
      try {
        video[isPaused ? 'pause' : 'play']()
      } catch {
        // Ignore errors
      }
    }

    if (isPaused) pause()
    else resume()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <div className="lp-HeroCarousel">
      <div className="lp-HeroCarousel-background">
        <div />
        <div />
      </div>

      {!shouldReduceMotion && SCREENS.length > 1 && (
        <div className="lp-HeroCarousel-autoPlayButton p-responsive">
          <AutoPlayButton
            autoPlayKey={currentScreenIndex}
            duration={visibleScreenDuration}
            onTimeout={onVisualTimeout}
            onPlayStateChange={onAutoPlayStateChange}
            ariaLabel={autoPlayButtonAriaLabel}
          />
        </div>
      )}

      <div className="lp-HeroCarousel-container">
        <div className="lp-HeroCarousel-content">
          <div
            ref={visualRef}
            className={`lp-HeroCarousel-visual ${!isVisualVisible ? 'lp-HeroCarousel-visual--hidden' : ''}`}
          >
            {visibleScreen?.visuals.length
              ? visibleScreen.visuals.map((visual, index) => {
                  const key = `visual-${visibleScreen.id}-${index}`
                  const src = visual.url[!visual.url.mobile || isDesktopView ? 'desktop' : 'mobile'] as string

                  return visual.type === 'image' ? (
                    <Image key={key} src={src} alt={visibleScreen.alt} />
                  ) : (
                    <video
                      key={key}
                      ref={videoRef}
                      autoPlay={!isPaused}
                      muted
                      playsInline
                      aria-label={visibleScreen.alt}
                      src={src}
                      onTimeUpdate={onVideoTimeUpdate}
                    />
                  )
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroCarousel
